import React from 'react';
import { Button } from '@amzn/awsui-components-console';
import { TaskStatus } from 'src/constants/commonEnums';
import { EQUAL_COMPARISION } from 'src/constants/ComponentConstants';
import { getDateAndTimeFromEpoch } from 'src/utils/CommonUtils';
import ShowSnowStatus from 'src/components/findEntity/ShowSNOWStatus';
import { UserIcon } from 'src/components/commonComponents/UserIcon';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';

export interface TaskRow {
	workflowName: string;
	workflowId: string;
	workflowExecutionId: string;
	nodes: string;
	entityType: string;
	taskName: string;
	taskType: string;
	taskExecutionId: string;
	status: TaskStatus;
	lastUpdateTime: number | string | bigint;
	startTime: number | string | bigint;
	startedBy: string;
	comment: string;
	attempt: number;
	childWorkflowId?: string;
	childWorkflowExecutionId?: string;
}

export const OPEN_STATUSES = [
	TaskStatus.FAILED,
	TaskStatus.PENDING_APPROVAL,
	TaskStatus.PENDING_REVIEW,
	TaskStatus.RUNNING,
	TaskStatus.SCHEDULED_FOR_DEPLOYMENT,
];

export const FILTERING_PROPERTIES = [
	{
		key: 'status',
		operators: EQUAL_COMPARISION,
		propertyLabel: 'Status',
		groupValuesLabel: 'Status values',
	},
];

export const FILTERING_OPTIONS = OPEN_STATUSES.map((status) => ({
	propertyKey: 'status',
	value: status,
	label: status
		.toLowerCase()
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (c) => c.toUpperCase()),
}));

export const CACHE_KEY = 'taskDashboard';
export const CACHE_TTL_MS = 2 * 60 * 60 * 1000;
export const BATCH_SIZE = 5;

export const EXECUTIONS_PAGE_SIZE = 200;
export const TASK_FETCH_CONCURRENCY = 10;
export const MAX_EXECUTION_PAGES = 50;
export const SKIPPABLE_EXECUTION_STATUSES = new Set(['COMPLETED']);

export const COLUMN_DEFINITIONS = [
	{
		id: 'workflowName',
		header: 'Workflow',
		cell: (item: TaskRow) => (
			<Button
				variant="inline-link"
				href={`#/entitySearch?workflowType=${item.workflowId}`}
				target="_blank"
			>
				{item.workflowName}
			</Button>
		),
		sortingField: 'workflowName',
		minWidth: 200,
	},
	{
		id: 'workflowExecutionId',
		header: 'Execution ID',
		cell: (item: TaskRow) => (
			<Button
				variant="inline-link"
				href={`#/findCycle?cycleId=${item.workflowExecutionId}&workflowType=${item.workflowId}`}
				target="_blank"
			>
				{item.workflowExecutionId}
			</Button>
		),
		sortingField: 'workflowExecutionId',
		minWidth: 160,
	},
	{
		id: 'nodes',
		header: 'Entities',
		cell: (item: TaskRow) => item.nodes || '—',
		sortingField: 'nodes',
		minWidth: 140,
	},
	{
		id: 'entityType',
		header: 'Entity Type',
		cell: (item: TaskRow) => item.entityType || '—',
		sortingField: 'entityType',
		minWidth: 100,
	},
	{
		id: 'taskName',
		header: 'Task Name',
		cell: (item: TaskRow) => item.taskName,
		sortingField: 'taskName',
		minWidth: 160,
	},
	{
		id: 'taskType',
		header: 'Task Type',
		cell: (item: TaskRow) => item.taskType || '—',
		sortingField: 'taskType',
		minWidth: 120,
	},
	{
		id: 'status',
		header: 'Status',
		cell: (item: TaskRow) => <ShowSnowStatus status={item.status} />,
		sortingField: 'status',
		minWidth: 90,
	},
	{
		id: 'lastUpdateTime',
		header: 'Last Updated Time',
		cell: (item: TaskRow) => getDateAndTimeFromEpoch(item.lastUpdateTime),
		sortingField: 'lastUpdateTime',
		minWidth: 160,
	},
	{
		id: 'startTime',
		header: 'Started Time',
		cell: (item: TaskRow) => getDateAndTimeFromEpoch(item.startTime),
		sortingField: 'startTime',
		minWidth: 160,
	},
	{
		id: 'startedBy',
		header: 'Started By',
		cell: (item: TaskRow) =>
			item.startedBy ? <UserIcon user={item.startedBy} /> : '—',
		sortingField: 'startedBy',
		minWidth: 90,
	},
	{
		id: 'attempt',
		header: 'Attempt',
		cell: (item: TaskRow) => item.attempt,
		sortingField: 'attempt',
		minWidth: 80,
	},
	{
		id: 'comment',
		header: 'Comment',
		cell: (item: TaskRow) => item.comment || '—',
		minWidth: 200,
	},
];

const HIDDEN_BY_DEFAULT = [
	'attempt',
	'comment',
	'entityType',
	'taskType',
	'nodes',
	'startTime',
];

export const DEFAULT_PREFERENCES = {
	pageSize: 25,
	wrapLines: true,
	contentDisplay: COLUMN_DEFINITIONS.map((col) => ({
		id: col.id,
		visible: !HIDDEN_BY_DEFAULT.includes(col.id),
	})),
	stripedRows: getDefaultStripedRows(),
};

export const HELP_PANEL_CONFIG = {
	title: 'About Task Dashboard',
	content: [
		'The Task Dashboard aggregates open tasks across all active workflows, showing failed, pending, and running tasks in one view.',
		'Data is cached for 2 hours. Use the Refresh button to fetch fresh data. All executions of every active workflow are scanned.',
		'Use the workflow multiselect and status dropdown to filter results. Click a workflow name to go to Entity Search, or an Execution ID to view the cycle.',
		'Toggle additional columns (Entities, Task Type, Attempt, Comment) via the gear icon.',
	],
	links: [{ href: '#/entitySearch', text: 'Entity Search' }],
};
